import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowClockwise, Check, Warning } from 'shared/icons';
import api from '../../../services/api';
import { FormatDecimal1, FormatDurationSec } from '../../../utils/format';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { Id } from '../../ui/Mono';
import { Skeleton } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import RouteMapView from './RouteMapView';
import {
    BuildRouteStops,
    ParseRouteComparison,
    ROUTE_ALGORITHM_MANUAL,
    RouteResultOf,
    RouteWarningsOf,
    SplitRouteWarnings,
} from './routeGeometry';

/**
 * Comparador de rotas (§6.6) — a tabela dos 6 métodos com o breakdown, o
 * desenho SOBRE O MAPA e a montagem da rota MANUAL.
 *
 * ── O desenho é o MAPA do galpão (M5-3, 11/08) ──────────────────────────────
 * Palavras do dono: *"o desenho da rota DEVE usar o sistema de mapa para
 * calcular rota… devemos ainda sim usar o mapa para calcular e exibir."* O
 * `RouteDiagram` (SVG paralelo no referencial do motor) MORREU: ele era uma
 * segunda descrição do galpão, ao lado da que o dono desenha. Agora o percurso
 * aparece por cima do `WarehouseMap` (`RouteMapView`), e a rota MANUAL é
 * montada tocando as paradas NO MAPA.
 *
 * ── Por que esta tela tem valor de decisão (e não é só treinamento) ──────────
 * Empate entre métodos é comum — o texto do dicionário diz onde olhar quando os
 * números se separam. A tela mostra o empate quando ele existe (a recomendação
 * sai empatada) e a diferença quando ela existe; em lugar nenhum ela escreve
 * que "dá tudo no mesmo".
 *
 * ── O que esta tela NUNCA faz ───────────────────────────────────────────────
 * Não calcula rota. Nenhum metro, segundo ou ordem sai daqui: tudo vem de
 * `POST /warehouse/routes/compare`. A rota MANUAL também é custeada pelo
 * motor — o Supervisor monta a SEQUÊNCIA no mapa, o backend cobra o preço
 * dela com o mesmo modelo dos outros cinco, e só assim a comparação é justa.
 */

// Wire do comparador: o MESMO item do resto do M5 — endereço por CÓDIGO — mais
// o crachá do equipamento (de onde parte o primeiro trecho, §6.4) e a sequência
// manual quando existe.
function buildComparePayload({ items, equipmentCode, manualSequence }) {
    return {
        items,
        ...(equipmentCode ? { equipment_code: equipmentCode } : {}),
        ...(manualSequence.length > 0 ? { manual_sequence: manualSequence } : {}),
    };
}

const MetricCell = ({ value, suffix = null }) => (
    <span className="font-mono tabular-nums text-sm">
        {value}
        {suffix ? <span className="ml-0.5 text-mono-400">{suffix}</span> : null}
    </span>
);

const RouteComparator = ({
    items = [],
    label,
    equipmentCode = null,
    chosenAlgorithm = null,
    onChoose = null,
    readOnly = false,
}) => {
    const [comparison, setComparison] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [selected, setSelected] = useState(chosenAlgorithm);
    const [manualMode, setManualMode] = useState(false);
    const [manualSequence, setManualSequence] = useState([]);
    // Só a sequência COMPLETA vai ao servidor: incompleta o motor recusa
    // (MANUAL_INCOMPLETE) e um POST por clique seria puro desperdício.
    const [submittedManual, setSubmittedManual] = useState([]);

    const stops = useMemo(() => BuildRouteStops(comparison), [comparison]);
    // O pai recria o array de itens a cada render; a identidade estável é o
    // CONTEÚDO. Sem isto o efeito de carga dispararia a cada render (POST em
    // laço) — a mesma armadilha que já custou uma correção no mapa do M1.
    const itemsKey = useMemo(() => JSON.stringify(items), [items]);
    const stableItems = useMemo(() => JSON.parse(itemsKey), [itemsKey]);

    // id do item (chave interna desta tabela) → código do LPN, para traduzir a
    // sequência escolhida em algo que quem alocou saiba reordenar.
    const lpnCodeByItemId = useMemo(
        () => new Map(stableItems.filter((item) => item?.id && item?.lpn_code).map((item) => [item.id, item.lpn_code])),
        [stableItems],
    );

    const load = useCallback(
        async (manual) => {
            if (stableItems.length === 0) {
                setComparison(null);
                return;
            }
            setLoading(true);
            setLoadError(null);
            try {
                const data = await api.compareWarehouseRoutes(
                    buildComparePayload({ items: stableItems, equipmentCode, manualSequence: manual }),
                );
                setComparison(ParseRouteComparison(data));
            } catch (error) {
                setLoadError(error?.message || label('wms.common.load_error'));
                setComparison(null);
            } finally {
                setLoading(false);
            }
        },
        [stableItems, equipmentCode, label],
    );

    // Recarrega quando os itens mudam (fatiar a alocação muda a rota) ou
    // quando uma sequência manual COMPLETA foi submetida.
    useEffect(() => {
        load(submittedManual);
    }, [load, submittedManual]);

    useEffect(() => {
        setSelected(chosenAlgorithm);
    }, [chosenAlgorithm]);

    const selectedResult = useMemo(() => {
        const explicit = selected ? RouteResultOf(comparison, selected) : null;
        if (explicit?.available) {
            return explicit;
        }
        const recommended = (comparison?.recommended ?? [])
            .map((algorithm) => RouteResultOf(comparison, algorithm))
            .find((result) => result?.available);
        return recommended ?? (comparison?.results ?? []).find((result) => result.available) ?? explicit ?? null;
    }, [comparison, selected]);

    // Montando a rota manual, o desenho NÃO empresta a linha de outro
    // algoritmo: enquanto a sequência não fecha, o que existe são as paradas.
    // Uma polilinha alheia embaixo dos cliques faria o Supervisor achar que
    // está corrigindo aquela rota — e ele está criando outra.
    const manualResult = useMemo(() => RouteResultOf(comparison, ROUTE_ALGORITHM_MANUAL), [comparison]);
    const mapResult = manualMode ? (manualResult?.available ? manualResult : null) : selectedResult;

    const warnings = useMemo(() => RouteWarningsOf(comparison, selectedResult), [comparison, selectedResult]);
    // Mapa sem desenho, endereço sem posição e câmbio de rede de segurança
    // mudam o que a tela está dizendo — vão em tarja, não em rodapé cinza.
    const { highlighted: mapWarnings, others: otherWarnings } = useMemo(() => SplitRouteWarnings(warnings), [warnings]);

    const toggleStop = (itemKey) => {
        const next = manualSequence.includes(itemKey)
            ? manualSequence.filter((key) => key !== itemKey)
            : [...manualSequence, itemKey];
        setManualSequence(next);
        // Completou a volta inteira: agora vale pedir o preço ao motor. Antes
        // disso ele recusaria (MANUAL_INCOMPLETE) — um POST por clique seria
        // desperdício puro.
        if (next.length === stops.length && stops.length > 0) {
            setSubmittedManual(next);
        } else if (submittedManual.length > 0) {
            setSubmittedManual([]);
        }
    };

    const startManual = () => {
        setManualMode(true);
        setManualSequence([]);
        setSubmittedManual([]);
        setSelected(ROUTE_ALGORITHM_MANUAL);
    };

    const columns = useMemo(
        () => [
            {
                key: 'algorithm',
                label: label('wms.route.method'),
                sortable: false,
                getValue: (row) => label(`wms.route.algorithm.${row.algorithm}`),
                render: (row) => (
                    <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-sm font-medium text-black dark:text-white">
                                {label(`wms.route.algorithm.${row.algorithm}`)}
                            </span>
                            {/* "Menor custo" ≠ "escolhida": o primeiro é o
                                veredito do motor (pode vir empatado em vários),
                                o segundo é a decisão do Supervisor. */}
                            {(comparison?.recommended ?? []).includes(row.algorithm) ? (
                                <Badge variant="success">{label('wms.route.recommended')}</Badge>
                            ) : null}
                            {row.algorithm === chosenAlgorithm ? (
                                <Badge variant="info">{label('wms.service_order.field.route_algorithm')}</Badge>
                            ) : null}
                        </div>
                        {!row.available && row.unavailable_reason ? (
                            <p className="max-w-[420px] text-xs text-mono-500 dark:text-mono-400">
                                {row.unavailable_reason.message}
                            </p>
                        ) : null}
                        {/* Força bruta que NÃO foi exata precisa dizer isso na
                            própria linha: uma coluna "exata" vazia passa
                            despercebida e o número seria lido como ótimo. */}
                        {row.available && row.algorithm === 'BRUTE_FORCE' && !row.exact ? (
                            <p className="max-w-[420px] text-xs text-warning-700 dark:text-warning-400">
                                {label('wms.route.brute_force_capped')}
                            </p>
                        ) : null}
                    </div>
                ),
            },
            {
                key: 'sequence',
                label: label('wms.service_order.field.items'),
                sortable: false,
                getValue: (row) => row.steps.map((step) => step.from_code).join(' → '),
                render: (row) =>
                    row.available && row.steps.length > 0 ? (
                        <div className="flex max-w-[280px] flex-wrap items-center gap-1">
                            {row.steps.map((step) => (
                                <Id
                                    key={`${row.algorithm}-${step.item_key}`}
                                    value={step.seq ?? step.order}
                                    className="rounded bg-mono-100 px-1.5 py-0.5 text-[11px] dark:bg-mono-900"
                                />
                            ))}
                        </div>
                    ) : (
                        <span className="text-mono-400">—</span>
                    ),
            },
            {
                key: 'distance',
                label: label('wms.route.distance'),
                sortable: false,
                getValue: (row) => row.breakdown.distance_m,
                render: (row) =>
                    row.available ? (
                        <MetricCell value={FormatDecimal1(row.breakdown.distance_m)} suffix="m" />
                    ) : (
                        <span className="text-mono-400">—</span>
                    ),
            },
            {
                key: 'travel',
                label: label('wms.route.time_travel'),
                sortable: false,
                getValue: (row) => row.breakdown.travel_sec,
                render: (row) =>
                    row.available ? (
                        <MetricCell value={FormatDurationSec(row.breakdown.travel_sec)} />
                    ) : (
                        <span className="text-mono-400">—</span>
                    ),
            },
            {
                key: 'vertical',
                label: label('wms.route.time_vertical'),
                sortable: false,
                getValue: (row) => row.breakdown.vertical_sec,
                render: (row) =>
                    row.available ? (
                        <MetricCell value={FormatDurationSec(row.breakdown.vertical_sec)} />
                    ) : (
                        <span className="text-mono-400">—</span>
                    ),
            },
            {
                key: 'handling',
                label: label('wms.route.time_handling'),
                sortable: false,
                getValue: (row) => row.breakdown.handling_sec,
                render: (row) =>
                    row.available ? (
                        <MetricCell value={FormatDurationSec(row.breakdown.handling_sec)} />
                    ) : (
                        <span className="text-mono-400">—</span>
                    ),
            },
            {
                key: 'total',
                label: label('wms.route.time_total'),
                sortable: false,
                getValue: (row) => row.breakdown.total_sec,
                render: (row) =>
                    row.available ? (
                        <span className="font-mono text-sm font-semibold tabular-nums text-black dark:text-white">
                            {FormatDurationSec(row.breakdown.total_sec)}
                        </span>
                    ) : (
                        <span className="text-mono-400">—</span>
                    ),
            },
            {
                key: 'action',
                label: label('wms.common.actions'),
                sortable: false,
                exportable: false,
                render: (row) => {
                    if (row.algorithm === ROUTE_ALGORITHM_MANUAL && !row.available && !readOnly) {
                        return (
                            <Button variant="secondary" size="sm" onClick={startManual}>
                                {label('wms.route.algorithm.MANUAL')}
                            </Button>
                        );
                    }
                    if (readOnly || !row.available || !onChoose) {
                        return null;
                    }
                    return (
                        <Button
                            variant={row.algorithm === chosenAlgorithm ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                setSelected(row.algorithm);
                                onChoose(
                                    row.algorithm,
                                    row.algorithm === ROUTE_ALGORITHM_MANUAL ? submittedManual : null,
                                    // A ORDEM é o que a escolha realmente
                                    // entrega: a confirmação da alocação não
                                    // aceita "sequência" nenhuma — ela grava os
                                    // itens na ordem em que forem enviados
                                    // (reordenar no backend quebraria a
                                    // amarração do par por `seq`). Sem devolver
                                    // isto, escolher rota não mudaria nada.
                                    //
                                    // Sai em CÓDIGO DE LPN, não no id interno
                                    // da prévia: quem recebe reordena linhas
                                    // LPN→destino, e o id daqui só existe
                                    // dentro desta tabela.
                                    (row.sequence || [])
                                        .map((key) => lpnCodeByItemId.get(key))
                                        .filter(Boolean),
                                );
                            }}
                        >
                            <Check size={14} className="mr-1.5" />
                            {label('wms.route.choose')}
                        </Button>
                    );
                },
            },
        ],
        [label, comparison, chosenAlgorithm, onChoose, readOnly, submittedManual, lpnCodeByItemId],
    );

    if (items.length === 0) {
        return null;
    }

    return (
        <section className="space-y-4">
            <header className="space-y-1">
                <h3 className="text-lg font-semibold text-black dark:text-white">{label('wms.route.title')}</h3>
                <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.route.description')}</p>
            </header>

            {/* A rota é calculada e desenhada SOBRE O MAPA (M5-3). Quando o
                desenho não sustenta a conta — galpão sem layout, endereço sem
                posição, `map_cell_m` fora do cadastro — o aviso vem ANTES da
                tabela: número estimado apresentado como exato é pior que
                número ausente. */}
            {mapWarnings.map((warning) => (
                <div
                    key={warning.rule}
                    className="flex items-start gap-3 rounded-lg border border-warning-300 bg-warning-soft p-3 dark:border-warning-800 dark:bg-warning-900/20"
                >
                    <Warning size={18} className="mt-0.5 shrink-0 text-warning-700 dark:text-warning-400" />
                    <p className="text-sm text-warning-800 dark:text-warning-300">{warning.message}</p>
                </div>
            ))}

            {otherWarnings.length > 0 ? (
                <ul className="space-y-1 rounded-lg border border-mono-200 bg-mono-50 p-3 text-xs text-mono-600 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400">
                    {otherWarnings.map((warning) => (
                        <li key={`${warning.rule}-${warning.message}`}>{warning.message}</li>
                    ))}
                </ul>
            ) : null}

            {loadError ? (
                <div className="flex flex-col items-start gap-3 rounded-lg border border-danger-300 bg-danger-soft p-4 dark:border-danger-800 dark:bg-danger-900/20">
                    <p className="text-sm text-danger-800 dark:text-danger-300">{loadError}</p>
                    <Button variant="secondary" size="sm" onClick={() => load(submittedManual)}>
                        <ArrowClockwise size={14} className="mr-1.5" />
                        {label('wms.common.retry')}
                    </Button>
                </div>
            ) : loading && !comparison ? (
                <Skeleton className="h-64 w-full" />
            ) : comparison ? (
                <>
                    <StandardTable
                        variant="minimal"
                        rows={comparison.results}
                        columns={columns}
                        rowKey={(row) => row.algorithm}
                        onRowClick={(row) => (row.available ? setSelected(row.algorithm) : null)}
                        rowClassName={(row) =>
                            row.algorithm === selectedResult?.algorithm
                                ? 'bg-info-soft/60 dark:bg-info-900/20'
                                : !row.available
                                  ? 'opacity-60'
                                  : ''
                        }
                        emptyMessage={label('wms.common.load_error')}
                        countLabel=""
                    />

                    {/* Empate é a regra dentro de uma rua e diferença real
                        entre ruas — o texto do dicionário diz as duas coisas. */}
                    <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.route.converge_hint')}</p>

                    {manualMode && !readOnly ? (
                        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-info-300 bg-info-soft p-3 dark:border-info-800 dark:bg-info-900/20">
                            <span className="text-sm text-info-800 dark:text-info-300">
                                {`${label('wms.route.manual_hint')} (${manualSequence.length}/${stops.length})`}
                            </span>
                            {/* Limpar zera as DUAS coisas: a sequência da tela
                                e a que já foi custeada. Sem isso a linha
                                MANUAL continuaria mostrando o preço de uma
                                rota que não existe mais. */}
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setManualSequence([]);
                                    setSubmittedManual([]);
                                }}
                            >
                                {label('wms.common.cancel')}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setManualMode(false)}>
                                {label('wms.common.close')}
                            </Button>
                        </div>
                    ) : null}

                    {/* O DESENHO da rota é o mapa do galpão (M5-3) — o mesmo
                        que o dono desenha e edita. Não existe mais diagrama
                        paralelo: o sistema tem UMA descrição do galpão. */}
                    <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-black dark:text-white">
                            {label('wms.route.map_title')}
                        </h4>
                        <RouteMapView
                            comparison={comparison}
                            result={mapResult}
                            stops={stops}
                            label={label}
                            manualMode={manualMode && !readOnly}
                            manualSequence={manualSequence}
                            onToggleStop={toggleStop}
                        />
                    </div>
                </>
            ) : null}
        </section>
    );
};

export default RouteComparator;
